import {
  UpdateType,
  type AbstractPowerSyncDatabase,
  type CrudEntry,
  type PowerSyncBackendConnector,
  type PowerSyncDatabase,
} from '@powersync/web';
import type { DeviceState } from './device-store';
import { currentAccessToken, supabase } from './supabase';
import { syncLog } from './debug-log';

const endpoint = import.meta.env.VITE_POWERSYNC_URL?.trim();
export const cloudSyncEnabled = import.meta.env.VITE_ENABLE_CLOUD_SYNC === 'true';

function requiredString(data: Record<string, unknown> | undefined, key: string): string {
  const value = data?.[key];
  if (typeof value !== 'string' || !value) throw new Error(`Missing ${key} in queued operation.`);
  return value;
}

function nullableString(data: Record<string, unknown> | undefined, key: string): string | null {
  const value = data?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function sourceRevisionId(
  entry: CrudEntry,
  revisionByRecord: ReadonlyMap<string, string>,
): string {
  const explicit = nullableString(entry.opData, 'source_revision_id');
  const inferred = revisionByRecord.get(entry.id);
  const revisionId = explicit ?? inferred;
  if (!revisionId) {
    throw new Error('A reminder mutation must be saved in the same local transaction as its encrypted revision.');
  }
  return revisionId;
}

class SupabasePowerSyncConnector implements PowerSyncBackendConnector {
  constructor(
    private readonly vaultId: string,
    private readonly deviceId: string,
    private readonly deviceAuthToken: string,
  ) {}

  async fetchCredentials() {
    if (!endpoint || !supabase) return null;
    const token = await currentAccessToken();
    if (!token) return null;
    return { endpoint, token };
  }

  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    if (!supabase) return;
    const transaction = await database.getNextCrudTransaction();
    if (!transaction) return;

    // Drain changes that belong to a different wallet on this browser (for example a
    // local-only wallet that never synced). Uploading them with this wallet's device is
    // rejected by the server and would jam the queue forever, blocking this wallet too.
    const queuedVaults = new Set(
      transaction.crud
        .map((entry) => entry.opData?.vault_id)
        .filter((value): value is string => typeof value === 'string'),
    );
    if (queuedVaults.size > 0 && !queuedVaults.has(this.vaultId)) {
      syncLog.warn(`draining ${transaction.crud.length} queued change(s) from another wallet on this browser.`);
      await transaction.complete();
      return;
    }
    syncLog.info(`uploading ${transaction.crud.length} change(s) for vault ${this.vaultId}.`);

    // Upload immutable encrypted revisions first. Reminder metadata is a side effect of
    // a specific revision and the server applies it only when that revision is canonical.
    // This prevents a losing conflict branch from changing the active reminder.
    const revisionByRecord = new Map<string, string>();
    for (const entry of transaction.crud) {
      if (entry.table !== 'record_revisions') continue;
      if (entry.op !== UpdateType.PUT) {
        throw new Error('Record revisions are immutable; only inserts may be uploaded.');
      }
      const data = entry.opData;
      const recordId = requiredString(data, 'record_id');
      const { error } = await supabase.rpc('commit_revision', {
        p_revision_id: entry.id,
        p_vault_id: requiredString(data, 'vault_id'),
        p_record_id: recordId,
        p_record_kind: requiredString(data, 'record_kind'),
        p_base_revision_id: nullableString(data, 'base_revision_id'),
        p_operation_id: requiredString(data, 'operation_id'),
        p_device_id: this.deviceId,
        p_device_auth_token: this.deviceAuthToken,
        p_crypto_version: Number(data?.crypto_version ?? 1),
        p_wrapped_record_key: requiredString(data, 'wrapped_record_key'),
        p_wrapped_record_key_nonce: requiredString(data, 'wrapped_record_key_nonce'),
        p_nonce: requiredString(data, 'nonce'),
        p_ciphertext: requiredString(data, 'ciphertext'),
        p_client_created_at: requiredString(data, 'client_created_at'),
        p_deleted: Number(data?.deleted ?? 0) === 1,
        p_resolves_conflict: Number(data?.resolves_conflict ?? 0) === 1,
      });
      if (error) {
        syncLog.error('commit_revision failed for revision', entry.id, error);
        throw error;
      }
      revisionByRecord.set(recordId, entry.id);
    }

    for (const entry of transaction.crud) {
      if (entry.table === 'record_revisions') continue;

      if (entry.table === 'reminders') {
        const revisionId = sourceRevisionId(entry, revisionByRecord);
        if (entry.op === UpdateType.DELETE) {
          const { error } = await supabase.rpc('delete_reminder', {
            p_record_id: entry.id,
            p_source_revision_id: revisionId,
            p_device_id: this.deviceId,
            p_device_auth_token: this.deviceAuthToken,
          });
          if (error) throw error;
        } else {
          const data = entry.opData;
          const { error } = await supabase.rpc('upsert_reminder', {
            p_record_id: entry.id,
            p_vault_id: requiredString(data, 'vault_id'),
            p_source_revision_id: revisionId,
            p_due_at: nullableString(data, 'due_at'),
            p_due_date: requiredString(data, 'due_date'),
            p_local_time: nullableString(data, 'local_time'),
            p_timezone: nullableString(data, 'timezone'),
            p_all_day: Number(data?.all_day ?? 0) === 1,
            p_recurrence: nullableString(data, 'recurrence') ?? 'none',
            p_mode: nullableString(data, 'mode') ?? 'cloud',
            p_device_id: this.deviceId,
            p_device_auth_token: this.deviceAuthToken,
          });
          if (error) throw error;
        }
        continue;
      }

      if (entry.table === 'attachments') {
        throw new Error('Secure attachment backup is not available yet. The encrypted file remains saved on this device.');
      }

      throw new Error(`Unexpected local write to read-only sync table: ${entry.table}`);
    }

    syncLog.info(`uploaded ${transaction.crud.length} change(s) for vault ${this.vaultId}.`);
    await transaction.complete();
  }
}

let connectedDeviceId: string | null = null;

export async function connectCloudSync(
  database: PowerSyncDatabase,
  device: Pick<DeviceState, 'vaultId' | 'deviceId' | 'deviceAuthToken'>,
): Promise<boolean> {
  if (!cloudSyncEnabled || !endpoint || !supabase) return false;
  const token = await currentAccessToken();
  if (!token) return false;
  if (connectedDeviceId === device.deviceId) return true;
  if (connectedDeviceId) await database.disconnect();
  await database.connect(new SupabasePowerSyncConnector(device.vaultId, device.deviceId, device.deviceAuthToken));
  connectedDeviceId = device.deviceId;
  return true;
}

export async function disconnectCloudSync(database: PowerSyncDatabase): Promise<void> {
  if (!connectedDeviceId) return;
  await database.disconnect();
  connectedDeviceId = null;
}
